import { asApiFailure, parseJsonObject, wasReported } from "@utils/apiFailure";
import { CLIENT_GONE_MESSAGE } from "./statusChange";

// What a refused PUT organizations/{id} (C5, API #426) means to the Edit
// Organization drawer. The API's codes, plus "gone" for the API's own 404 and
// "other" for anything the drawer does not explain itself.
export type EditOrganizationRefusalKind =
    | "organization_changed"
    | "email_in_use"
    | "email_registered"
    | "gone"
    | "other";

export interface EditOrganizationRefusal {
    kind: EditOrganizationRefusalKind;
    message: string;
    // email_in_use only: the active client that holds the email.
    existingOrganizationId?: string;
}

// useOrganizationApi toasts every failure but a 409 and the API's own 404,
// with the server's own words, so this line names no cause. It must still be
// said: a drawer that just re-enables Save reads as if nothing went wrong.
// The Edit Location drawer says the same (editLocation.ts).
export const NOT_SAVED =
    "Your changes were not saved. Your edits are still here, so you can try again.";

// The drawer's own words for a coded 409 whose body carried no message.
const FALLBACK: Record<
    "organization_changed" | "email_in_use" | "email_registered",
    string
> = {
    organization_changed:
        "Someone else changed this client after you opened it. Load the latest details and make your changes again.",
    email_in_use:
        "This email already belongs to another client. Use a different contact email.",
    email_registered:
        "This email already has a sign-in, so its owner couldn't set up this client's account. Use a different contact email.",
};

const isKnownCode = (code: unknown): code is keyof typeof FALLBACK =>
    typeof code === "string" && code in FALLBACK;

export const editOrganizationRefusal = (
    error: unknown,
): EditOrganizationRefusal => {
    const failure = asApiFailure(error);

    // The API's own 404 is the one useOrganizationApi did not toast (it
    // carried the API's body). A 404 useApi did toast (no body, a gateway's
    // page) says nothing about the client, so it is only "not saved".
    if (failure?.status === 404 && !wasReported(error)) {
        return { kind: "gone", message: CLIENT_GONE_MESSAGE };
    }

    if (failure?.status !== 409) {
        return { kind: "other", message: NOT_SAVED };
    }

    const body = parseJsonObject(failure.detail);
    const code = body?.error;
    const message =
        typeof body?.message === "string" ? body.message.trim() : "";

    if (!isKnownCode(code)) {
        // A 409 this page does not know: the toast was suppressed, so the
        // API's sentence (or ours) is the only word on it.
        return { kind: "other", message: message || NOT_SAVED };
    }

    const refusal: EditOrganizationRefusal = {
        kind: code,
        message: message || FALLBACK[code],
    };
    const existingOrganizationId = body?.existingOrganizationId;

    if (code === "email_in_use" && typeof existingOrganizationId === "string") {
        refusal.existingOrganizationId = existingOrganizationId;
    }

    return refusal;
};
